// Brand context — the active brand drives every brand-scoped page (My Brand,
// Products, Competitors, Creatives, Calendar, Ad Strategist). Switching is a
// server-side flip on user_profile.active_brand_id; data hooks that depend on
// the active brand re-fire by subscribing to activeBrand.id.
//
// Mounted inside CreditsProvider in App.tsx so useAuth works.

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useAuth } from './useAuth';
import { auth } from '@/lib/firebase';
import {
  listBrands as apiListBrands,
  activateBrand as apiActivateBrand,
  createBrand as apiCreateBrand,
  deleteBrand as apiDeleteBrand,
  type BrandRecord,
} from '@/lib/api';

interface BrandContextType {
  /** All non-deleted brands the user owns, oldest-first. */
  brands: BrandRecord[];
  /** The currently active brand, or null while loading / pre-signin. */
  activeBrand: BrandRecord | null;
  loading: boolean;
  /** True once a brands fetch has COMPLETED SUCCESSFULLY at least once.
   *  AuthGate must only treat `brands: []` as "genuinely no brands → run
   *  onboarding" when this is true — an empty list from a not-yet-loaded or
   *  errored fetch must NOT bounce a brand-owning user into /brands/new. */
  loaded: boolean;
  /** True when the last brands fetch failed (after retries). AuthGate shows a
   *  retry screen instead of redirecting so a transient error can't strand a
   *  brand-owning user in the "Add a brand" wizard. */
  loadError: boolean;
  /** True when the brands fetch returned 403 email_not_verified even after a
   *  forced token refresh — the email genuinely isn't verified. AuthGate routes
   *  to /auth/verify-email instead of the misleading "network" error screen. */
  emailUnverified: boolean;
  /** Activate a brand server-side, then HARD-RELOAD to `target` (or the
   *  current path). The reload is intentional: switching brand mid-flow
   *  leaves stale React state, in-flight API requests carrying the old
   *  brand context, and cached generated images everywhere. A full reload
   *  is the safest way to reset all of that. */
  switchBrand: (brandId: string, target?: string) => Promise<void>;
  /** Re-pull from the server (no reload). Internal — use only when you
   *  need the list refreshed without a brand switch (e.g. after rename). */
  refresh: () => Promise<void>;
  /** Create a brand. Does NOT activate or reload — caller chains
   *  `switchBrand(newId, target)` for the standard "create + jump into it"
   *  flow. Throws on plan-cap or network error. */
  addBrand: (name?: string) => Promise<BrandRecord>;
  /** Soft-delete a brand. If it was active, the server auto-activates the
   *  oldest remaining brand. Triggers a hard reload because active brand
   *  may have changed. */
  removeBrand: (brandId: string) => Promise<string | null>;
}

const BrandContext = createContext<BrandContextType | null>(null);

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  // Depend on user?.id (stable string), NOT user (object reference). See
  // useCredits for the full explanation — Supabase's onAuthStateChange
  // fires several times per page load, each setUser creates a new ref,
  // and with [user] as the dep this useEffect would re-fire 3-5× per
  // mount → /api/brands called 3-5×. Keyed on user?.id, fires once per
  // signed-in-user change.
  const userId = user?.id ?? null;
  const [brands, setBrands] = useState<BrandRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [emailUnverified, setEmailUnverified] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) {
      // No user yet (initial mount before Firebase Auth restores the
      // session). DON'T set loading=false here — the auth bridge is still
      // resolving and the brands fetch hasn't really completed. If we
      // flipped loading to false, AuthGate would see (user=loaded,
      // brands=[], brandLoading=false) for an instant during refresh and
      // hard-redirect the user to /brands/new even though they actually
      // own brands. Keep loading=true; AuthGate's user-null check handles
      // the signed-out case before falling through to the brands check.
      setBrands([]);
      return;
    }
    setLoading(true);
    setLoadError(false);
    setEmailUnverified(false);
    // Retry a couple times before giving up. Right after sign-in the Firebase
    // token / session can be momentarily not-ready, producing a transient 401
    // or network error. Without the retry, that transient failure collapses to
    // "0 brands" and bounces a brand-owning user to /brands/new (the reported
    // "always lands on Add A Brand" bug). A genuinely brand-less user's fetch
    // SUCCEEDS with [] on the first attempt, so this adds zero latency there.
    let lastErr: unknown = null;
    let forcedRefresh = false;   // email_not_verified → force-refresh the token ONCE (stale-claim case)
    // 5 attempts with exponential backoff (0.4 → 3.2s, ~6s total window). A
    // fresh signup in a cold browser (esp. incognito) commonly fails the first
    // try or two: a Cloud Run cold start (min-instances=0 → ~5-15s first
    // request), a Firebase token not yet minted, or the fetch aborted by the
    // post-signup navigation. The old 3-attempt / ~0.9s window gave up before
    // those cleared and flashed the "Couldn't load your brands" error screen on
    // the user's very first load. The wider window absorbs them silently. A
    // user who genuinely has [] brands SUCCEEDS on attempt 1 → zero added
    // latency; the retries only cost time on an actual failure.
    const MAX_ATTEMPTS = 5;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      try {
        const list = await apiListBrands();
        setBrands(list);
        setLoaded(true);       // confirmed-good load — AuthGate may now gate on brands.length
        setLoadError(false);
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        // invite_required is owned by InviteLayer (mapFailure already fired
        // the sp:invite-required event) — don't retry-spam it.
        if (err instanceof Error && err.message === 'invite_required') break;
        // email_not_verified is a 403 STATE error, not a transient — retrying
        // with the same cached token can NEVER 200 (this is exactly why widening
        // the retry window did nothing). Almost always it's a stale ID token:
        // the user JUST verified (client `emailVerified` is already true, so
        // AuthGate let them past Phase 2.5) but the token's `email_verified`
        // claim was baked false at mint time. Force ONE token refresh to pick up
        // the new claim, then retry. If it's STILL unverified after a fresh
        // token, the email genuinely isn't verified → flag it so AuthGate routes
        // to the verify page (NOT the "network hiccup" screen).
        if (err instanceof Error && err.message === 'email_not_verified') {
          if (!forcedRefresh) {
            forcedRefresh = true;
            try { await auth.currentUser?.getIdToken(true); } catch { /* ignore */ }
            continue;   // retry immediately with the fresh token
          }
          try { await auth.currentUser?.reload(); } catch { /* ignore */ }
          setEmailUnverified(true);
          setLoading(false);
          return;
        }
        // Exponential backoff before the next attempt — NO force token-refresh
        // here. The real cause of first-request auth failures was clock skew (a
        // freshly-minted token's `iat` a few seconds in the server's future →
        // 401), now fixed backend-side with a `leeway` on jwt.decode. A
        // force-refresh would MINT another future-`iat` token, making skew
        // worse; the cached token is the better one to reuse. The backoff
        // still covers cold starts, token-not-ready, and transient blips.
        if (attempt < MAX_ATTEMPTS - 1) {
          await new Promise((r) => setTimeout(r, Math.min(3200, 400 * 2 ** attempt)));
        }
      }
    }
    // All attempts failed. Do NOT setBrands([]) — leave whatever we had so a
    // transient failure doesn't flash "0 brands". `loaded` stays as-is; AuthGate
    // reads loadError and shows a retry screen instead of redirecting.
    console.error('[useBrand] list failed after retries:', lastErr);
    setLoadError(true);
    setLoading(false);
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  // First-login recovery for the invite-redeem race. On a fresh signup the
  // parallel /api/brands call can 403 `invite_required` BEFORE InviteLayer
  // finishes redeeming the stashed code — useBrand breaks out of its retry loop
  // (invite_required is a state error, not a transient) and sets loadError, so
  // AuthGate shows the "Couldn't load your brands" screen. InviteLayer redeems
  // the code a beat later and dispatches `sp:invite-redeemed` on success; re-run
  // the fetch then so the error screen clears itself instead of stranding the
  // (now-invited) user until they manually hit Retry. Idempotent — a no-op for
  // a user whose brands already loaded.
  useEffect(() => {
    const onRedeemed = () => { void refresh(); };
    window.addEventListener('sp:invite-redeemed', onRedeemed);
    return () => window.removeEventListener('sp:invite-redeemed', onRedeemed);
  }, [refresh]);

  const switchBrand = useCallback(async (brandId: string, target?: string) => {
    await apiActivateBrand(brandId);
    // Hard reload: every brand-scoped surface (gallery, products, scheduler,
    // ad-strategist, wizard) holds in-memory state keyed to the OLD brand.
    // A full reload is the only deterministic way to flush it all + cancel
    // any in-flight requests carrying the old brand context.
    //
    // Default landing = /create (the user's home base after switching).
    // If they switch from /brands, /brands/new, or /onboarding-ish surfaces
    // we DON'T want to dump them back where they came from — those are
    // brand-management surfaces, not brand-using ones. Caller can override
    // via `target` for the create/activate flow.
    const here = window.location.pathname;
    const isMgmtPath = here === '/brands' || here.startsWith('/brands/');
    const dest = target ?? (isMgmtPath ? '/create' : here + window.location.search);
    window.location.assign(dest);
  }, []);

  const addBrand = useCallback(async (name?: string) => {
    const created = await apiCreateBrand(name);
    await refresh();
    return created;
  }, [refresh]);

  const removeBrand = useCallback(async (brandId: string) => {
    const { activeBrandId } = await apiDeleteBrand(brandId);
    // Hard reload (same reasoning as switchBrand). If the user just
    // deleted their last brand the server returns activeBrandId=null —
    // jump straight to /brands/new so AuthGate doesn't bounce them
    // there one navigation later.
    const target = activeBrandId
      ? window.location.pathname + window.location.search
      : '/brands/new';
    window.location.assign(target);
    return activeBrandId;
  }, []);

  const activeBrand = brands.find(b => b.isActive) ?? null;

  return (
    <BrandContext.Provider value={{
      brands,
      activeBrand,
      loading,
      loaded,
      loadError,
      emailUnverified,
      switchBrand,
      refresh,
      addBrand,
      removeBrand,
    }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand(): BrandContextType {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within BrandProvider');
  return ctx;
}
